import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../context/AppContext";
import { useCopy } from "../../context/CopyContext";
import { Gem, ConciergeBell } from "lucide-react";
import Header from "../Header";
import Heading from "../Heading";
import Button from "../Button";
import MultiLineCopy from "../MultiLineCopy";
import PoweredBy from "../PoweredBy";
import ItemsModalView from "./ItemsModalView";

const MembershipSelectionView: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const { getCopy } = useCopy();
  const [showItemsModal, setShowItemsModal] = useState(false);

  // Mock email checking - simulate checking against a database
  const checkEmailExists = (emailAddress: string): boolean => {
    // Mock logic: emails ending with @brand.com or @pentatonic.com are "existing members"
    const existingDomains = ["@brand.com", "@pentatonic.com"];
    return existingDomains.some(domain => emailAddress.toLowerCase().includes(domain));
  };

  const handleBecomeAMember = () => {
    dispatch({ type: "SET_GUEST", payload: false });
    dispatch({ type: "SET_MEMBER", payload: true });
    // Clear any previously selected reward method when switching to member
    dispatch({ type: "SET_REWARD_METHOD", payload: undefined });
    
    // Check if inline sign up is enabled and email exists
    if (state.settings.inlineSignUp && state.userEmail) {
      const emailExists = checkEmailExists(state.userEmail);
      
      if (emailExists) {
        // Existing member - navigate to sign in
        console.log("Existing member detected - navigating to kiosk sign in:", state.userEmail);
        dispatch({ type: "SET_IS_SIGNUP", payload: false }); // This is a login flow
        navigate("/kiosk-sign-in");
      } else {
        // New member - navigate to sign up
        console.log("New member detected - navigating to kiosk sign up:", state.userEmail);
        dispatch({ type: "SET_IS_SIGNUP", payload: true }); // This is a signup flow
        navigate("/kiosk-sign-up");
      }
    } else {
      // Default flow - go to member welcome (existing behavior)
      dispatch({ type: "SET_IS_SIGNUP", payload: true }); // Flag for signup flow
      console.log("User chose to become a member - navigating to member welcome");
      navigate("/member-welcome");
    }
  };

  const handleGuest = () => {
    dispatch({ type: "SET_GUEST", payload: true });
    dispatch({ type: "SET_MEMBER", payload: false });
    // Clear any previously selected reward method when switching to guest
    dispatch({ type: "SET_REWARD_METHOD", payload: undefined });
    
    console.log("User chose guest option - navigating to reward method selection");
    navigate("/reward-method-selection");
  };

  const handleBack = () => {
    navigate("/email-capture");
  };

  const handleItemsClick = () => {
    setShowItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setShowItemsModal(false);
  };

  return (
    <div
      className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden"
      data-node-id="126:1561"
    >
      {/* Content Container */}
      <div className="relative z-10 flex-1 flex flex-col ">
        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={state.itemsCount > 0}
              itemsNumber={state.itemsCount.toString()}
              
              
              onItemsClick={handleItemsClick}
            />
          </div>
        </div>

        {/* Main Content Area - Animated with 3-row grid */}
        <motion.div 
          className="flex-1 grid grid-rows-3 gap-0 mx-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Rows 1 & 2 - Main heading spanning two rows */}
          <div className="row-span-2 flex items-center justify-center">
            <div className="text-center w-full max-w-[920px]">
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ 
                  duration: 0.6, 
                  ease: [0.25, 0.1, 0.25, 1]
                }}
              >
                <Heading 
                  title={getCopy('membershipSelection.heading')}
                  subtitle={getCopy('membershipSelection.subheading')}
                />
              </motion.div>
            </div>
          </div>

          {/* Row 3 - Member type selection */}
          <div className="flex items-start justify-center">
            <motion.div
              className="w-full max-w-[920px]"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                delay: 0.2,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              {/* Choose member type heading */}
              <div className="text-center mb-10">
                <h3 className="font-brand-primary font-medium text-[32px] leading-[42px] text-center text-brand-text-primary tracking-[-0.32px]">
                  {getCopy('membershipSelection.chooseMemberTypeHeading')}
                </h3>
              </div>

              {/* Member type cards */}
              <div className="flex flex-row gap-10 items-stretch justify-start">
                {/* Become a Member Card */}
                <motion.div
                  className="flex-1 bg-brand-background border border-brand-border rounded-3xl p-8 flex flex-col gap-14 items-center justify-center cursor-pointer hover:border-brand-primary transition-colors duration-200"
                  onClick={handleBecomeAMember}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex flex-col gap-6 items-center justify-center w-full">
                    <div className="flex flex-col gap-2 items-center justify-start w-full">
                      <div className="w-16 h-16 flex items-center justify-center">
                        <Gem size={64} stroke="var(--brand-text-primary)" strokeWidth={1} />
                      </div>
                      <div className="flex flex-col gap-2 items-start justify-start text-center w-full">
                        <h4 className="font-brand-primary font-normal text-[24px] leading-[31px] text-brand-text-primary w-full">
                          {getCopy('membershipSelection.becomeMemberCard.title')}
                        </h4>
                        <p className="font-brand-primary font-light text-[18px] leading-[23px] text-brand-text-secondary w-full">
                          {getCopy('membershipSelection.becomeMemberCard.description')}
                        </p>
                      </div>
                    </div>
                    <div className="border border-brand-text-primary rounded-full px-4 py-1">
                      <span className="font-brand-primary font-normal text-[18px] leading-[31px] text-brand-text-primary">
                        {getCopy('membershipSelection.becomeMemberCard.badge')}
                      </span>
                    </div>
                  </div>
                </motion.div>

                {/* Guest Card */}
                <motion.div
                  className="flex-1 bg-brand-background border border-brand-border rounded-3xl p-8 flex flex-col gap-14 items-center justify-center cursor-pointer hover:border-brand-primary transition-colors duration-200"
                  onClick={handleGuest}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex flex-col gap-6 items-center justify-center w-full">
                    <div className="flex flex-col gap-2 items-center justify-start w-full">
                      <div className="w-16 h-16 flex items-center justify-center">
                        <ConciergeBell size={64} stroke="var(--brand-text-primary)" strokeWidth={1} />
                      </div>
                      <div className="flex flex-col gap-2 items-start justify-start text-center w-full">
                        <h4 className="font-brand-primary font-normal text-[24px] leading-[31px] text-brand-text-primary w-full">
                          {getCopy('membershipSelection.guestCard.title')}
                        </h4>
                        <MultiLineCopy
                          text={getCopy('membershipSelection.guestCard.description')}
                          className="font-brand-primary font-light text-[18px] leading-[23px] text-brand-text-secondary w-full"
                        />
                      </div>
                    </div>
                    <div className="border border-brand-text-primary rounded-full px-3 py-1">
                      <span className="font-brand-primary font-normal text-[18px] leading-[31px] text-brand-text-primary">
                        {getCopy('membershipSelection.guestCard.badge')}
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Bottom Section - Back button and footer */}
        <div className="flex flex-col gap-16 items-center mx-20 mb-10">
          {/* Back Button */}
          <div className="flex justify-center w-full max-w-[282px]">
            <Button variant="tertiary" onClick={handleBack}>
              {getCopy('membershipSelection.buttons.tertiary')}
            </Button>
          </div>

          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>

      {/* Items Modal */}
      <ItemsModalView
        isOpen={showItemsModal}
        onClose={handleCloseItemsModal}
      />
    </div>
  );
};

export default MembershipSelectionView;
